/**
 * Operating-account selection (person → N accounts).
 * Replaces the old investimento / individualizada type switcher.
 */

export const SELECTED_ACCOUNT_STORAGE_KEY = "@WalletBC:SelectedOperatingAccountId";

/** BaaS console Contas selection — must not share IB Carteira session storage. */
export const BAAS_CONSOLE_ACCOUNT_STORAGE_KEY = "@WalletBC:BaasConsoleSelectedAccountId";

/**
 * Set after a fresh login so PrivateApp can show the Conta card picker
 * (DOCUMENTO | TIPO | NUMERO) before entering Carteira when multiple accounts exist.
 */
export const IB_ACCOUNT_PICK_PENDING_KEY = "@WalletBC:IbAccountPickPending";

/** Fired when Carteira IB active account id is stored (HomeAccountSelector). */
export const IB_ACTIVE_ACCOUNT_CHANGED_EVENT = "valut:ib-active-account-changed";

export const isIbAccountPickPending = () => {
  try {
    return sessionStorage.getItem(IB_ACCOUNT_PICK_PENDING_KEY) === "1";
  } catch {
    return false;
  }
};

export const setIbAccountPickPending = (pending = true) => {
  try {
    if (pending) {
      sessionStorage.setItem(IB_ACCOUNT_PICK_PENDING_KEY, "1");
    } else {
      sessionStorage.removeItem(IB_ACCOUNT_PICK_PENDING_KEY);
    }
  } catch {
    // ignore
  }
};

export const clearIbAccountPickPending = () => setIbAccountPickPending(false);

/** @deprecated Prefer SELECTED_ACCOUNT_STORAGE_KEY — kept for session migration. */
export const HOME_ACCOUNT_STORAGE_KEY = "@BCBankline:HomeAccountType";

/**
 * Query for IB account-scoped GETs (`/v1/ib/accounts/balances`, statements, …).
 * MobileService proxy must honor `account_id` so saldo is not the person-active row.
 */
export const normalizeAccountIdToken = (value) =>
  String(value || "")
    .trim()
    .replace(/^acct_/i, "")
    .toLowerCase();

export const accountIdsMatch = (left, right) => {
  const a = String(left || "").trim();
  const b = String(right || "").trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const na = normalizeAccountIdToken(a);
  const nb = normalizeAccountIdToken(b);
  return Boolean(na) && Boolean(nb) && na === nb;
};

export const getIbAccountQueryParams = (accountId) => {
  const id =
    accountId != null && String(accountId).trim() !== ""
      ? String(accountId).trim()
      : getStoredSelectedAccountId();
  if (!id || isLegacyAccountTypeId(id)) return {};
  return { account_id: String(id) };
};

export const getStoredBaasConsoleAccountId = () => {
  try {
    return sessionStorage.getItem(BAAS_CONSOLE_ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredBaasConsoleAccountId = (accountId) => {
  const nextId = accountId != null && String(accountId).trim() !== "" ? String(accountId).trim() : null;
  try {
    if (nextId) {
      sessionStorage.setItem(BAAS_CONSOLE_ACCOUNT_STORAGE_KEY, nextId);
    } else {
      sessionStorage.removeItem(BAAS_CONSOLE_ACCOUNT_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

export const getStoredSelectedAccountId = () => {
  try {
    const modern = sessionStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY);
    if (modern) return modern;

    // Migrate legacy type switcher values into the new key once.
    const legacy = sessionStorage.getItem(HOME_ACCOUNT_STORAGE_KEY);
    if (legacy === "individualizada" || legacy === "investimento") {
      sessionStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, legacy);
      return legacy;
    }
  } catch {
    // ignore
  }
  return null;
};

export const emitIbActiveAccountChanged = (accountId) => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, {
          detail: { accountId: accountId ? String(accountId) : null },
        }),
      );
    }
  } catch {
    // ignore
  }
};

export const setStoredSelectedAccountId = (accountId, { emitEvent = true } = {}) => {
  const nextId = accountId ? String(accountId) : null;
  const prevId = getStoredSelectedAccountId();
  const prevNormalized = prevId != null && prevId !== "" ? String(prevId) : null;

  // Same id → no write / no event. Re-firing on every HomeAccountSelector mount caused
  // Dashboard to refetch account+balance, swap to full-page skeleton (unmounting the
  // transfer modal), remount the selector, and loop ("tela piscando").
  if (nextId === prevNormalized) {
    return;
  }

  try {
    if (nextId) {
      sessionStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, nextId);
    } else {
      sessionStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
    }
    sessionStorage.removeItem("@BCBankline:Balance");
  } catch {
    // ignore
  }

  // Same-tab listeners (Contas virtuais / Stables scope) — storage event is cross-tab only.
  if (emitEvent !== false) {
    emitIbActiveAccountChanged(nextId);
  }
};

/**
 * @deprecated Legacy type ids — only for MobileService Pix payloads that still expect account_type.
 */
export const isLegacyAccountTypeId = (id) =>
  id === "investimento" || id === "individualizada";

export const toPixAccountTypePayload = (accountIdOrType) =>
  accountIdOrType === "individualizada" ? "individualizada" : "investimento";

export const getAccountTypeLabel = (id) => {
  if (id === "individualizada") return "Conta individualizada";
  if (id === "investimento") return "Conta investimento";
  if (!id) return "Conta";
  // Real operating account ids — show a short readable token.
  const raw = String(id);
  return raw.length > 18 ? `Conta ${raw.slice(0, 8)}…${raw.slice(-4)}` : `Conta ${raw}`;
};

/** @deprecated Use dynamic account lists from fetchOperatingAccounts / BaasInstanceContext. */
export const ACCOUNT_TYPE_OPTIONS = [
  { id: "investimento", label: "Conta investimento" },
  { id: "individualizada", label: "Conta individualizada" },
];

/** @deprecated */
export const getStoredAccountType = () => {
  const stored = getStoredSelectedAccountId();
  return stored === "individualizada" ? "individualizada" : "investimento";
};
